import React from 'react';
import Utilities from '@util/Utilities';
import FormButton from '@app/component/form/button/FormButton';
import { CalendarDaysIcon, PresentationChartBarIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import Message from '@app/component/message/Message';
import { Link, matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import {
	MarketStateFilterInputProvider,
} from '@app/site/secured/rateshopper/rates/provider/MarketStateFilterInputProvider';

const ParityDashboardPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {
		return <MarketStateFilterInputProvider companyHotel={companyHotel}>
			<Dashboard companyHotel={companyHotel} />
		</MarketStateFilterInputProvider>;
	}

	return <Message state="warning">Please refresh the page</Message>;

});

const Dashboard: React.FunctionComponent<{ companyHotel: CompanyHotel }>
	= ({ companyHotel }) => {

	const navigate = useNavigate();
	const location = useLocation();

	const isParity = !!matchPath('/secured/rateshopper/parity', location.pathname);
	const isRoomMapping = !!matchPath('/secured/rateshopper/parity/room-mapping', location.pathname);
	const isRatesPromotion = !!matchPath('/secured/rateshopper/parity/rates-promotion', location.pathname);

	if (!!companyHotel) {

		return <>
			<header className="bg-white shadow-sm">
				<div className="mx-auto max-w-7xl py-4 px-4 sm:px-6 lg:px-8">
					<div className="md:flex md:items-center md:justify-between">
						<div className="min-w-0 flex-1">
							<h1 className="text-lg font-semibold leading-6 text-gray-900">
								Parity
							</h1>
						</div>
						<div className="md:hidden">
							<div className="mt-1">
								<select aria-label="Select a tab" className="w-full input"
												onChange={(e) => navigate(e.target.value)}>
									<option value="/secured/rateshopper/parity" selected={isParity}>Parity</option>
									<option value="/secured/rateshopper/room-mapping" selected={isRoomMapping}>Room Mapping</option>
								</select>
							</div>
						</div>
						<div className="hidden md:block">
							<div className="mt-4 md:flex space-y-2 md:space-y-0 md:space-x-2.5 md:ml-4 md:mt-0">
								<div data-tooltip-id="parity-tooltip"
										 data-tooltip-content="You need to activate Brand.com!">
									<FormButton state="white" isLink to="/secured/rateshopper/parity"
															extraClassName={Utilities.classNames('font-normal py-1.5', isParity ? ' text-blue-700 bg-blue-50' : '')}>
										<PresentationChartBarIcon className="w-4 h-4 mr-2 mt-0.5" />
										Parity
									</FormButton>
								</div>

								<div>
									<FormButton state="white" isLink to="/secured/rateshopper/parity/rates-promotion"
															extraClassName={Utilities.classNames('font-normal py-1.5', isRatesPromotion ? ' text-blue-700 bg-blue-50' : '')}>
										<CalendarDaysIcon className="w-4 h-4 mr-2" />
										Rateshopper
									</FormButton>
								</div>

								<div className="isolate inline-flex rounded-md shadow-sm">
									<Link to="/secured/rateshopper/room-mapping" data-tooltip-id="room-mapping-tooltip"
												data-tooltip-content="Room Mapping"
												className={Utilities.classNames('relative inline-flex items-center -ml-px rounded-r-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 rounded-l-md', isRoomMapping ? ' text-blue-700 bg-blue-50' : 'text-gray-900 bg-white')}>
										<Square2StackIcon className="w-5 h-5" />
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
				<Tooltip id="coming-soon-tooltip" />
				<Tooltip id="room-mapping-tooltip" />
			</header>
			<Outlet />
		</>;

	}
	return <Message state="error"
									title="There was an error showing the dashboard!. Please try again later or contact support!" />;
};


export default ParityDashboardPage;